import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Button, Input, Modal, Text } from '../../ui'
import { deleteWorkspace, type Workspace } from '../../../lib/api'
import { SettingsSection } from './SettingsSection'

type DangerSectionProps = {
  workspace: Workspace
}

type DeleteForm = {
  confirmName: string
}

export function DangerSection({ workspace }: DangerSectionProps) {
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const form = useForm<DeleteForm>({ defaultValues: { confirmName: '' } })

  function closeModal() {
    setOpen(false)
    setError(null)
    form.reset()
  }

  async function onSubmit(data: DeleteForm) {
    setError(null)

    if (data.confirmName.trim() !== workspace.name) {
      form.setError('confirmName', { message: 'Workspace name does not match' })
      return
    }

    const result = await deleteWorkspace(workspace.id)
    if (!result.success) {
      setError(result.error.message)
      return
    }

    window.location.href = '/'
  }

  return (
    <SettingsSection
      id="danger"
      title="Danger zone"
      description="Permanently delete this workspace and all of its content."
    >
      <div className="rounded-2xl border border-red-200 bg-red-50/60 p-5">
        <Text variant="label" className="mb-2 text-red-900">
          Delete workspace
        </Text>
        <Text variant="body-sm" className="mb-4 text-red-900/80">
          This permanently deletes {workspace.name}, including collections, articles, team
          memberships, and billing records. This cannot be undone.
        </Text>
        <Button type="button" variant="outline" onClick={() => setOpen(true)}>
          Delete workspace
        </Button>
      </div>

      <Modal open={open} onClose={closeModal}>
        <Text variant="h2" className="mb-2 pr-8 text-xl">
          Delete workspace
        </Text>
        <Text variant="body-sm" className="mb-6 text-foreground/60">
          Type the workspace name <strong>{workspace.name}</strong> to confirm.
        </Text>

        <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)}>
          <Input
            label="Workspace name"
            autoComplete="off"
            error={form.formState.errors.confirmName?.message}
            {...form.register('confirmName', { required: 'Workspace name is required' })}
          />

          {error && (
            <Text variant="caption" className="text-red-600">
              {error}
            </Text>
          )}

          <div className="flex flex-col gap-3 sm:flex-row">
            <Button type="button" variant="ghost" fullWidth onClick={closeModal}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="outline"
              fullWidth
              loading={form.formState.isSubmitting}
              className="border-red-300 text-red-700 hover:bg-red-50"
            >
              Delete workspace
            </Button>
          </div>
        </form>
      </Modal>
    </SettingsSection>
  )
}
